<template>
  <div class="friendly-captcha-container">
    <div 
      ref="captchaWidget"
      class="frc-captcha"
      :data-sitekey="siteKey"
      data-lang="de"
      data-theme="light"
      :data-start="autoStart ? 'auto' : 'manual'"
    ></div>
    
    <div v-if="error" class="captcha-error">
      {{ error }}
    </div>
  </div>
</template>

<script>
export default {
  name: 'CFriendlyCaptcha',
  
  props: {
    siteKey: {
      type: String,
      required: true
    },
    autoStart: {
      type: Boolean,
      default: true
    }
  },
  
  data() {
    return {
      widgetInstance: null,
      error: null,
      isScriptLoaded: false,
      scriptLoadPromise: null
    }
  },
  
  mounted() {
    this.loadFriendlyCaptcha()
  },

  updated() {
    if (this.$refs.captchaWidget && !this.widgetInstance && window.friendlyChallenge) {
      this.$nextTick(() => {
        this.initWidget()
      })
    }
  },
  
  activated() {
    if (this.widgetInstance) {
      this.reset()
    } else {
      this.loadFriendlyCaptcha()
    }
  },
  
  beforeDestroy() {
    this.cleanup()
  },
  
  deactivated() {
    // Handle keep-alive component deactivation
    this.cleanup()
  },
  
  methods: {
    loadFriendlyCaptcha() {
      if (window.friendlyChallenge) {
        this.initWidget()
        return
      }
      
      if (this.scriptLoadPromise) {
        this.scriptLoadPromise.then(() => this.initWidget())
        return
      }
      
      this.scriptLoadPromise = this.loadScript()
      this.scriptLoadPromise
        .then(() => {
          this.isScriptLoaded = true
          this.initWidget()
        })
        .catch((error) => {
          console.error('Failed to load Friendly Captcha script:', error)
          this.error = 'Failed to load Friendly Captcha. Please refresh the page and try again.'
          this.$emit('error', this.error)
        })
    },
    
    loadScript() {
      return new Promise((resolve, reject) => {
        const existingScript = document.querySelector('script[src*="friendly-challenge"]')
        if (existingScript) {
          if (window.friendlyChallenge) {
            resolve()
          } else {
            existingScript.addEventListener('load', resolve)
            existingScript.addEventListener('error', reject)
          }
          return
        }
        
        const script = document.createElement('script')
        script.src = 'https://cdn.jsdelivr.net/npm/friendly-challenge@0.9.16/widget.min.js'
        script.async = true
        script.defer = true
        script.onload = resolve
        script.onerror = reject
        
        document.head.appendChild(script)
      })
    },
    
    initWidget() {
      if (!window.friendlyChallenge || !this.$refs.captchaWidget) {
        return
      }

      if (this.widgetInstance && this.$refs.captchaWidget.querySelector('.frc-captcha-solution')) {
        return
      }
      
      try {
        this.cleanup()
        this.$refs.captchaWidget.innerHTML = ''

        this.widgetInstance = new window.friendlyChallenge.WidgetInstance(this.$refs.captchaWidget, {
          startMode: this.autoStart ? 'auto' : 'none',
          doneCallback: (solution) => {
            this.$emit('solved', solution)
            this.error = null
          },
          errorCallback: (error) => {
            console.error('Friendly Captcha error:', error)
            this.error = 'Captcha verification failed. Please try again.'
            this.$emit('error', this.error)
          }
        })
        
        this.$emit('ready')
      } catch (error) {
        console.error('Error initializing Friendly Captcha:', error)
        this.error = 'Failed to initialize captcha. Please refresh the page.'
        this.$emit('error', this.error)
      }
    },
    
    start() {
      if (this.widgetInstance) {
        this.widgetInstance.start()
      }
    },
    
    reset() {
      if (this.widgetInstance) {
        this.widgetInstance.reset()
        this.error = null
      }
    },
    
    getSolution() {
      const input = this.$refs.captchaWidget.querySelector('input[name="frc-captcha-solution"]')
      return input ? input.value : ''
    },
    
    cleanup() {
      if (this.widgetInstance) {
        try {
          this.widgetInstance.destroy()
        } catch (e) { }
        this.widgetInstance = null
      }
    }
  }
}
</script>

<style scoped>
.friendly-captcha-container {
  margin: 16px 0;
}

.frc-captcha {
  border-radius: 4px;
}

.captcha-error {
  color: #d32f2f;
  font-size: 14px;
  margin-top: 8px;
  padding: 8px;
  background-color: #ffebee;
  border: 1px solid #ffcdd2;
  border-radius: 4px;
}
</style>

<style>
.frc-captcha {
  font-family: inherit;
}

.frc-button {
  font-family: inherit;
}
</style>
